import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { ContractsService } from '../../../services/contracts.service';
import { PaymentService } from '../../../services/payment.service';
import { ContractFreezePayload, ContractFreezeResponse } from '../../../models/Contract';

@Component({
  selector: 'app-contract-freeze-modal',
  templateUrl: './contract-freeze-modal.component.html',
  styleUrls: ['./contract-freeze-modal.component.css']
})
export class ContractFreezeModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() contractId: number | null = null;
  /** Texto auxiliar (ex.: número do contrato) */
  @Input() subtitle = '';

  @Output() closed = new EventEmitter<void>();
  @Output() frozen = new EventEmitter<ContractFreezeResponse>();

  /** Formato `YYYY-MM-DD` — pré-preenchido com o dia atual. */
  startDateOnly = '';

  /** Período fixo: 30, 60 ou 90 dias */
  readonly dayOptions = [30, 60, 90] as const;
  daysPreset: 30 | 60 | 90 = 30;

  isSubmitting = false;
  validationMessage: string | null = null;

  constructor(
    private contractsService: ContractsService,
    private paymentService: PaymentService
  ) {}

  ngOnInit(): void {
    if (this.isOpen) {
      document.body.classList.add('modal-open');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        document.body.classList.add('modal-open');
        this.resetForm();
      } else {
        document.body.classList.remove('modal-open');
      }
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('modal-open');
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(ev: KeyboardEvent): void {
    if (!this.isOpen || ev.key !== 'Escape' || this.isSubmitting) {
      return;
    }
    this.close();
  }

  onBackdropClick(ev: MouseEvent): void {
    const t = ev.target as HTMLElement;
    if (t?.classList?.contains('cfm-backdrop') && !this.isSubmitting) {
      this.close();
    }
  }

  /** Hoje no calendário local (`YYYY-MM-DD`). */
  private todayDateOnly(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private resetForm(): void {
    this.startDateOnly = this.todayDateOnly();
    this.daysPreset = 30;
    this.validationMessage = null;
    this.isSubmitting = false;
  }

  setDaysPreset(value: 30 | 60 | 90): void {
    this.daysPreset = value;
  }

  close(): void {
    this.closed.emit();
  }

  /** Início do dia (00:00:00.000) no fuso local → ISO UTC. */
  private dateOnlyToStartIso(dateStr: string): string | undefined {
    if (!dateStr?.trim()) return undefined;
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return undefined;
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!y || !m || !d) return undefined;
    const local = new Date(y, m - 1, d, 0, 0, 0, 0);
    if (isNaN(local.getTime())) return undefined;
    return local.toISOString();
  }

  private buildPayload(): ContractFreezePayload | null {
    this.validationMessage = null;

    const startDate = this.dateOnlyToStartIso(this.startDateOnly);
    if (!startDate) {
      this.validationMessage = 'Informe a data de início.';
      return null;
    }

    const n = this.daysPreset;
    return { dias: n, days: n, startDate };
  }

  confirm(): void {
    if (this.contractId == null) {
      this.validationMessage = 'Contrato inválido.';
      return;
    }
    const payload = this.buildPayload();
    if (!payload) return;

    this.isSubmitting = true;
    const id = this.contractId;
    this.contractsService
      .freezeContract(id, payload)
      .pipe(
        switchMap((freezeRes: ContractFreezeResponse) => {
          const start =
            freezeRes.freezeStartUtc ?? (freezeRes as any).FreezeStartUtc;
          const end = freezeRes.freezeEndUtc ?? (freezeRes as any).FreezeEndUtc;
          if (!start || !end) {
            return of(freezeRes);
          }
          return this.contractsService.getContractComplete(id).pipe(
            switchMap(data => {
              const digits = this.payerDocumentDigitsFromComplete(data);
              if (!digits) {
                return of(freezeRes);
              }
              return this.paymentService
                .cancelPendingBoletosInFreezeWindow(digits, start, end)
                .pipe(map(() => freezeRes), catchError(() => of(freezeRes)));
            }),
            catchError(() => of(freezeRes))
          );
        })
      )
      .subscribe({
        next: res => {
          this.isSubmitting = false;
          this.frozen.emit(res);
          this.close();
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
  }

  /** CPF/CNPJ do pagador (cliente ou motorista) para localizar cobranças no Asaas. */
  private payerDocumentDigitsFromComplete(data: any): string | null {
    const c = data?.customer;
    const d = data?.driver;
    const custDoc = c?.documentNumber ?? c?.document ?? c?.cpfCnpj;
    const driverDoc = d?.cpfCnpj;
    const raw = String(custDoc || driverDoc || '').replace(/\D/g, '');
    if (raw.length === 11 || raw.length === 14) return raw;
    return null;
  }
}
